import React, { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Translate } from "orion-components/i18n";
import { useSelector } from "react-redux";
import { LineChart } from "@mui/x-charts/LineChart"; // Import MUI LineChart
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { getDir } from "orion-components/i18n/Config/selector";

const propTypes = {
	check: PropTypes.object,
	locale: PropTypes.string
};

const defaultProps = {
	check: {},
	locale: "en"
};

const styles = {
	container: {
		color: "#FFF",
		margin: "0 13px"
	},
	header: {
		width: "100%",
		display: "flex",
		justifyContent: "space-between"
	},
	statusContainer: {
		display: "flex",
		alignItems: "center"
	},
	dot: dir => ({
		width: 12,
		height: 12,
		borderRadius: "50%",
		...(dir === "rtl" ? { marginLeft: 8 } : { marginRight: 8 })
	}),
	chartContainer: {
		width: "100%",
		height: 200,
		marginTop: -40
	}
};

const ClientConnection = () => {
	const locale = useSelector(state => state.i18n.locale);
	const dir = useSelector(state => getDir(state));
	const bytesReceived = useSelector(state => state.systemHealth.bytesReceived);
	const totalBytesReceived = useSelector(state => state.systemHealth.totalBytesReceived);
	const connected = useSelector(state => state.systemHealth.connected);
	const online = useSelector(state => state.systemHealth.online);

	const [status, setStatus] = useState(!online ? "offline" : connected ? "connected" : "disconnected");
	const [currentTime, setCurrentTime] = useState(moment().startOf("second").valueOf());

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentTime(moment().startOf("second").valueOf());
		}, 1000);

		return () => clearInterval(interval); // Cleanup on unmount
	}, []);

	useEffect(() => {
		if (!online) {
			setStatus("offline");
		} else if (connected) {
			setStatus("connected");
		} else {
			setStatus("disconnected");
		}
	}, [connected, online]);

	// Generate a dataset that includes future timestamps for the next 2 minutes
	const now = currentTime;
	const twoMinutesAgo = moment(now).subtract(2, "minutes").startOf("second").valueOf();
	const fullDataset = Array.from({ length: 120 }, (_, i) => {
		const timestamp = twoMinutesAgo + i * 1000;
		const dataPoint = bytesReceived.find(data => data.timestamp === timestamp);
		return {
			timestamp,
			bytes: dataPoint ? dataPoint.bytes : 0
		};
	}).filter(data => data.bytes !== null);

	return (
		<Fragment>
			<div style={styles.container}>
				<div style={styles.header}>
					<div style={styles.statusContainer}>
						<p
							style={{ ...styles.dot(dir), backgroundColor: online && connected ? "#76E25C" : "#E85858" }}
						/>
						<Translate value={`global.dock.systemHealth.clientConnection.status.${status}`} />
					</div>
					<Translate
						value={`global.dock.systemHealth.clientConnection.totalDataReceived`}
						count={
							totalBytesReceived >= 1e9
								? `${(totalBytesReceived / 1e9).toFixed(2)} GB`
								: totalBytesReceived >= 1e6
									? `${(totalBytesReceived / 1e6).toFixed(2)} MB`
									: totalBytesReceived >= 1e3
										? `${(totalBytesReceived / 1e3).toFixed(0)} KB`
										: `${totalBytesReceived} B`
						}
					/>
				</div>
				<div style={styles.chartContainer}>
					<LineChart
						skipAnimation
						height={styles.chartContainer.height}
						xAxis={[
							{
								dataKey: "timestamp",
								valueFormatter: value => moment(value).locale(locale).format("HH:mm:ss"), // Force English locale
								label: null, // Remove x-axis labels
								min: twoMinutesAgo,
								max: now,
								reverse: dir === "rtl"
							}
						]}
						yAxis={[
							{
								id: "bytes",
								valueFormatter: value => {
									if (value >= 1e6) return `${(value / 1e6).toFixed(2)} MB`;
									if (value >= 1e3) return `${(value / 1e3).toFixed(0)} KB`;
									return `${value} B`;
								},
								tickLabelStyle: {
									textAnchor: "end"
								}
							}
						]}
						leftAxis={dir === "rtl" ? null : "bytes"}
						rightAxis={dir === "rtl" ? "bytes" : null}
						series={[
							{
								dataKey: "bytes",
								label: "Bytes Received",
								showMark: false,
								color: "rgba(75, 192, 192, 1)",
								curve: "catmullRom",
								connectNulls: true
							}
						]}
						dataset={fullDataset}
						slotProps={{ legend: { hidden: true } }}
						margin={dir === "rtl" ? { left: 10, right: 60 } : { left: 60, right: 10 }}
						sx={{
							[`.${axisClasses.root}`]: {
								[`.${axisClasses.tick}, .${axisClasses.line}`]: {
									stroke: "#FFF"
								},
								[`.${axisClasses.tickLabel}`]: {
									fill: "#FFF"
								}
							}
						}}
					/>
				</div>
			</div>
		</Fragment>
	);
};

ClientConnection.propTypes = propTypes;
ClientConnection.defaultProps = defaultProps;

export default ClientConnection;
